import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail } from "lucide-react";
import { fetchMenuItems } from "../lib/api";

export default function Header() {
  const [menuItems, setMenuItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef(null);

  const handleMouseEnter = (href) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHoveredItem(href);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
    }, 250); // 250ms delay to make it super smooth
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    async function loadMenu() {
      const items = await fetchMenuItems();
      if (items.length > 0) {
        // Group menu items into a tree structure
        const topLevel = items.filter((item) => !item.parent_slug);
        const subItems = items.filter((item) => item.parent_slug);
        const tree = topLevel.map((parent) => {
          const parentSlug = parent.href.replace(/^\//, "");
          const children = subItems.filter((child) => child.parent_slug === parentSlug);
          
          // Prepend parent page itself to the dropdown children list so it is accessible
          const combinedChildren = children.length > 0
            ? [{ label: parent.href === "/om-john" ? "Min historie" : parent.label, href: parent.href }, ...children]
            : [];
            
          return { ...parent, children: combinedChildren };
        });
        setMenuItems(tree);
      } else {
        // Fallback menu structure if CMS is empty or loading
        const fallback = [
          { label: "Forside", href: "/" },
          { label: "Clairvoyance", href: "/clairvoyance-online" },
          { label: "Hypnose", href: "/hypnose" },
          {
            label: "Kurser og Uddannelse",
            href: "/kurser-og-uddannelse",
            children: [
              { label: "Kurser / Workshop", href: "/kurser-workshop" },
              { label: "Shamanistisk Uddannelse", href: "/shamanistisk-uddannelse" },
              { label: "Clairvoyanceuddannelse", href: "/clairvoyanceuddannelse" },
            ],
          },
          {
            label: "Calmars Univers",
            href: "/calmars-univers",
            children: [
              { label: "Klarsyn Online", href: "/klarsyn-online" },
              { label: "Klarsynsaften Online", href: "/klarsynsaften-online" },
            ],
          },
          { label: "Butik", href: "/butik" },
          { label: "Referencer", href: "/referencer" },
          {
            label: "Om John",
            href: "/om-john",
            children: [{ label: "Mød mig", href: "/moed-mig" }],
          },
          { label: "Kontakt", href: "/kontakt" },
        ];

        const tree = fallback.map((parent) => {
          if (parent.children && parent.children.length > 0) {
            return {
              ...parent,
              children: [{ label: parent.href === "/om-john" ? "Min historie" : parent.label, href: parent.href }, ...parent.children]
            };
          }
          return parent;
        });
        setMenuItems(tree);
      }
    }
    loadMenu();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const isActive = (href) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className={`sticky top-0 z-50 w-full bg-white border-b border-neutral-200/50 transition-shadow duration-200 ${isScrolled ? "shadow-md" : ""}`}>
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center focus:outline-none shrink-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=85,format=auto/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-logo.png"
            alt="John Calmar – Clairvoyance & Hypnose i Aarhus"
            className="h-11 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation (min-width: 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-1">
          {menuItems.map((item) => (
            <div
              key={item.href}
              className="relative flex items-center"
              onMouseEnter={() => handleMouseEnter(item.href)}
              onMouseLeave={handleMouseLeave}
            >
              {item.children && item.children.length > 0 ? (
                <>
                  <Link
                    to={item.href}
                    className={`px-2.5 py-2 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 transition-all duration-200 focus:outline-none cursor-pointer ${
                      isActive(item.href) || hoveredItem === item.href
                        ? "text-neutral-950"
                        : "text-neutral-600 hover:text-neutral-950"
                    }`}
                  >
                    {item.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${hoveredItem === item.href ? "rotate-180" : ""}`} />
                  </Link>

                  {/* Dropdown Menu */}
                  <div
                    className={`absolute top-full left-0 mt-1 w-56 bg-white border border-neutral-200/80 rounded-2xl shadow-xl py-2 transition-all duration-300 ease-out z-50 before:content-[""] before:absolute before:-top-3 before:left-0 before:right-0 before:h-3 ${
                      hoveredItem === item.href
                        ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
                        : "opacity-0 translate-y-2 scale-95 pointer-events-none"
                    }`}
                  >
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className={`block px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors duration-150 ${
                          location.pathname === child.href
                            ? "text-neutral-950"
                            : "text-neutral-600 hover:text-neutral-950 hover:bg-neutral-50"
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <Link
                  to={item.href}
                  className={
                    item.href === "/kontakt" || item.label === "Kontakt"
                      ? "ml-3 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-brand-600 hover:bg-brand-700 text-white rounded-full transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer inline-flex items-center whitespace-nowrap"
                      : `px-2.5 py-2 text-xs font-semibold uppercase tracking-wider transition-all duration-200 ${
                          isActive(item.href)
                            ? "text-neutral-950"
                            : "text-neutral-600 hover:text-neutral-950"
                        }`
                  }
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* Burgermenu Button (max-width: 1199px) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="block min-[1200px]:hidden p-2 text-neutral-600 hover:text-neutral-950 focus:outline-none transition-colors duration-200"
          aria-label="Menu"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation (max-width: 1199px) */}
      {isOpen && (
        <div className="block min-[1200px]:hidden border-t border-neutral-200/60 bg-white/98 backdrop-blur-lg py-6 px-6 space-y-4 max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="space-y-2">
            {menuItems.map((item) => (
              <div key={item.href} className="space-y-1">
                {item.children && item.children.length > 0 ? (
                  <>
                    <button
                      onClick={() =>
                        setActiveDropdown(activeDropdown === item.href ? null : item.href)
                      }
                      className={`w-full px-4 py-3 text-left text-base font-medium rounded-xl flex items-center justify-between transition-colors ${
                        isActive(item.href)
                          ? "text-brand-700"
                          : "text-neutral-700 hover:text-neutral-950"
                      }`}
                    >
                      {item.label}
                      <ChevronDown
                        className={`w-5 h-5 transition-transform duration-200 ${
                          activeDropdown === item.href ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Mobile Dropdown Children */}
                    {activeDropdown === item.href && (
                      <div className="pl-4 space-y-1 bg-brand-100/20 rounded-xl py-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={`block px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                              location.pathname === child.href
                                ? "text-brand-700"
                                : "text-neutral-600 hover:text-neutral-950"
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={
                      item.href === "/kontakt" || item.label === "Kontakt"
                        ? "block px-4 py-3 text-center text-base font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-xl transition-colors shadow-sm mt-2"
                        : `block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                            isActive(item.href)
                              ? "text-brand-700"
                              : "text-neutral-700 hover:text-neutral-950"
                          }`
                    }
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Contact Info */}
          <div className="pt-6 border-t border-brand-200/40 space-y-3 text-sm text-neutral-600">
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-brand-600" />
              <a href="tel:40938593" className="hover:text-brand-700 transition-colors whitespace-nowrap">+45 40 93 85 93</a>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-brand-600" />
              <a href="mailto:info@johncalmar.dk" className="hover:text-brand-700 transition-colors whitespace-nowrap">info@johncalmar.dk</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
